import { Input } from "@visiblethread/react-components";
import ChoiceGroup, { choiceGroupLayouts } from "components/ChoiceGroup";
import Flex, { GAP_CLASS } from "components/Flex";
import { ALIGN_CLASS, DIRECTION_CLASS } from "components/Flex/Flex";
import { QUESTION_TYPE, YES_NO_VALUES } from "constants/proposals";
import { CircleAlert } from "lucide-react";
import { arrayOf, bool, func, instanceOf, number, oneOfType, shape, string } from "prop-types";

import classes from "./GateQuestionGroup.module.scss";

const buildChoiceOptions = (question) => {
  const values =
    question.type === QUESTION_TYPE.YES_NO
      ? [YES_NO_VALUES.YES, YES_NO_VALUES.NO]
      : question.options || [];

  return values.map((value) => ({ value, label: value }));
};

const GateQuestionGroup = ({
  gate,
  ordinalLabel,
  showHeaders = false,
  disabled = false,
  answers,
  onAnswerChange,
  invalidQuestionIds,
}) => {
  const renderQuestionLabel = (question) => (
    <Flex
      alignItems={ALIGN_CLASS.center}
      gap={GAP_CLASS.GAP_150}
      className={classes["question__label_row"]}
    >
      <span className={classes["question__text"]}>{question.text}</span>
      {invalidQuestionIds.has(question.id) && (
        <Flex
          alignItems={ALIGN_CLASS.center}
          gap={GAP_CLASS.GAP_050}
          className={classes["question__required"]}
        >
          <CircleAlert size={14} />
          <span>Required</span>
        </Flex>
      )}
    </Flex>
  );

  const renderInput = (question) => {
    const value = answers[question.id] ?? "";

    if (question.type === QUESTION_TYPE.FREE_TEXT) {
      return (
        <Flex
          direction={DIRECTION_CLASS.column}
          alignItems={ALIGN_CLASS.stretch}
          gap={GAP_CLASS.GAP_200}
        >
          {renderQuestionLabel(question)}
          <Input
            id={`gate-question-${question.id}`}
            aria-label={question.text}
            value={value}
            disabled={disabled}
            error={invalidQuestionIds.has(question.id)}
            onChange={(nextValue) => onAnswerChange(question.id, nextValue)}
          />
        </Flex>
      );
    }

    return (
      <ChoiceGroup
        layout={choiceGroupLayouts.column}
        question={renderQuestionLabel(question)}
        options={buildChoiceOptions(question)}
        value={value}
        onChange={(nextValue) => onAnswerChange(question.id, nextValue)}
        disabled={disabled}
      />
    );
  };

  return (
    <Flex
      direction={DIRECTION_CLASS.column}
      alignItems={ALIGN_CLASS.stretch}
      gap={GAP_CLASS.GAP_350}
      className={classes["group"]}
    >
      {showHeaders && (
        <Flex
          alignItems={ALIGN_CLASS.center}
          gap={GAP_CLASS.GAP_200}
          className={classes["group__header"]}
        >
          <span className={classes["group__transition"]}>{`${gate.title} → ${gate.toTitle}`}</span>
          <span className={classes["group__divider"]} />
          <span className={classes["group__ordinal"]}>{`${ordinalLabel} Gate Questions`}</span>
        </Flex>
      )}

      <Flex
        direction={DIRECTION_CLASS.column}
        alignItems={ALIGN_CLASS.stretch}
        gap={GAP_CLASS.GAP_400}
        className={classes["group__questions"]}
      >
        {gate.questions.map((question) => (
          <div key={question.id} className={classes["question"]}>
            {renderInput(question)}
          </div>
        ))}
      </Flex>
    </Flex>
  );
};

GateQuestionGroup.propTypes = {
  gate: shape({
    columnId: oneOfType([number, string]),
    title: string,
    toTitle: string,
    questions: arrayOf(
      shape({
        id: number.isRequired,
        type: string.isRequired,
        text: string.isRequired,
        options: arrayOf(string),
      })
    ).isRequired,
  }).isRequired,
  ordinalLabel: string.isRequired,
  showHeaders: bool,
  disabled: bool,
  answers: shape({}).isRequired,
  onAnswerChange: func.isRequired,
  invalidQuestionIds: instanceOf(Set).isRequired,
};

export default GateQuestionGroup;
